import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { ReviewsTrustBadgeWidgetDefinition } from "@apps/reviews/definitions";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import IntersectionObserverInjector from "@ui-utils/Injector/IntersectionObserverInjector";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import { parseOptionalBooleanDataAttr } from "@ui-utils/Injector/utils";

import { LoggerInterface } from "@interfaces/LoggerInterface";

class ReviewsTrustBadgeWidgetInjector {
    private moi: MutationObserverInjector;
    private ioi: IntersectionObserverInjector;

    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly dynamicImportManager: DynamicImportManager
    ) {
        this.moi = new MutationObserverInjector(MutationObserver, {
            mutationTarget: document.body,
            targetSelector: this.placeholderSelector,
        });
        this.ioi = new IntersectionObserverInjector(
            IntersectionObserver,
            this.moi,
            {
                rootMargin: "100px 0px 0px 0px",
            }
        );
    }

    start(): void {
        this.logger.debug("ReviewsTrustBadgeWidgetInjector start", {
            placeholderSelector: this.placeholderSelector,
        });

        this.ioi.targets$.subscribe((placeholder) => {
            this.logger?.debug(
                "ReviewsTrustBadgeWidgetInjector found placeholder",
                {
                    placeholder,
                }
            );

            const widgetName = REVIEWS_WIDGET_NAMES.REVIEWS_TRUST_BADGE_WIDGET;

            void (async () => {
                const ReviewsTrustBadgeWidget =
                    await this.dynamicImportManager.importDefault(
                        ReviewsTrustBadgeWidgetDefinition,
                        true
                    );

                const widget = new ReviewsTrustBadgeWidget(
                    {
                        target: placeholder,
                        isWidgetShadowRootEnabled: parseOptionalBooleanDataAttr(
                            placeholder,
                            WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                        ),
                    },
                    this.logger
                );

                await widget.init();

                this.eventsCollector.pushEvent(
                    this.analyticsEventsFactory.createWidgetInjected(widgetName)
                );

                dispatchSdkEvent(new WidgetInjectedSdkEvent(widgetName));
            })();
        });

        this.ioi.start();
    }
}

export default ReviewsTrustBadgeWidgetInjector;
